import { all, call, put, takeLatest } from 'redux-saga/effects';

import * as errorHandlerActions from '@/shared/modules/errorHandler/actions';
import config from '@/config';
import { IS_LOCAL_ENVIRONMENT } from '@/shared/config/env';
import { ROUTES_API } from '@/shared/config/routes';
import { removeAccessToken, setAccessToken } from '@/shared/services/accessToken';
import { processRequest, RequestMethod } from '@/shared/services/api';
import { joinBaseUrl } from '@/shared/utils/url';
import * as sharedActions from '../store/actions';
import { sharedTypes } from '../store/constants';

import type { Action, Event, LoginResponse } from '../types';

interface SendEventPayload {
	data: Event;
}

export default function* rootSharedSaga() {
	yield all([
		takeLatest(sharedTypes.LOGIN_REQUEST, handleLoginRequest),
		takeLatest(sharedTypes.SEND_EVENT_REQUEST, handleSendEventRequest),
	]);
}

export function* handleLoginRequest() {
	try {
		if (!config.oauth_username || !config.oauth_password) {
			yield put(sharedActions.loginError('Username and password are required'));
			return;
		}

		const clientSecret = config.oauth_client_secret;

		if (!config.api_url) {
			yield put(sharedActions.loginError('OAuth base URL is required'));
			return;
		}

		if (!clientSecret) {
			yield put(sharedActions.loginError('OAuth client secret is required'));
			return;
		}

		const formdata = new FormData();
		formdata.append('client_id', config.oauth_client_id);
		formdata.append('client_secret', clientSecret);
		formdata.append('grant_type', 'password');
		formdata.append('username', config.oauth_username);
		formdata.append('password', config.oauth_password);

		const requestOptions = {
			method: 'POST',
			body: formdata,
		};

		const loginUrl = joinBaseUrl(config.api_url, ROUTES_API.OAUTH2_TOKEN);
		const response: Response = yield call(fetch, loginUrl, requestOptions);
		const responseText: string = yield call([response, 'text']);

		if (!response.ok) {
			throw new Error(responseText || `Login request failed with status ${response.status}`);
		}

		let parsed: Record<string, unknown> | null = null;

		try {
			parsed = JSON.parse(responseText) as Record<string, unknown>;
		} catch (_error) {
			parsed = null;
		}

		const loginResponse: LoginResponse = {
			accessToken: typeof parsed?.access_token === 'string' ? parsed.access_token : null,
			raw: responseText,
			parsed,
		};

		if (loginResponse.accessToken) {
			yield call(
				setAccessToken,
				loginResponse.accessToken,
				typeof parsed?.expires_in === 'number' ? parsed.expires_in : undefined,
			);
		} else {
			yield call(removeAccessToken);
		}

		yield put(sharedActions.loginSuccess(loginResponse));
	} catch (e) {
		yield call(removeAccessToken);
		yield put(errorHandlerActions.handleError(e, 'ignore'));
		yield put(sharedActions.loginError(e instanceof Error ? e.message : 'Login request failed'));
	}
}

export function* handleSendEventRequest({ payload }: Action<SendEventPayload>) {
	try {
		const requestData = payload?.data;

		if (!requestData) {
			yield put(sharedActions.sendEventError());
			return;
		}

		if (IS_LOCAL_ENVIRONMENT) {
			return;
		}

		yield call(processRequest, ROUTES_API.SEND_EVENT, RequestMethod.POST, requestData);
	} catch (e) {
		yield put(errorHandlerActions.handleError(e, 'ignore'));
		yield put(sharedActions.sendEventError());
	}
}
